import type { ReactNode } from 'react'
import { Result } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth, usePermission } from '@/hooks'
import type { Permission } from '@/constants'

interface RequirePermissionProps {
  children: ReactNode
  /** 単一権限。permissions と併用された場合は permissions が優先される。 */
  permission?: Permission
  /** 複数権限のうち、いずれか1つを持っていれば許可（OR）。 */
  permissions?: Permission[]
  /** true なら所属自治体の ocr_use=true を要求する。OCR 関連ルートで使う。 */
  requireOcrUse?: boolean
  fallback?: ReactNode
}

/**
 * ロールではなく機能権限（PERMISSION_KEYS）でガードする。
 * 後端 {@code @PreAuthorize("hasAuthority('PERM_XXX')")} と対になる UI 側ガード。
 */
export default function RequirePermission({
  children,
  permission,
  permissions,
  requireOcrUse,
  fallback,
}: RequirePermissionProps) {
  const { t } = useTranslation()
  const perm = usePermission()
  const { session } = useAuth()

  let allowed: boolean
  if (permissions && permissions.length > 0) {
    allowed = perm.hasAnyPermission(...permissions)
  } else if (permission) {
    allowed = perm.hasPermission(permission)
  } else {
    allowed = true
  }

  if (allowed && requireOcrUse && !session?.ocrUse) {
    allowed = false
  }

  if (!allowed) {
    return fallback ? <>{fallback}</> : (
      <Result
        status="403"
        title={t('accessDenied')}
        subTitle={t('accessDeniedDesc')}
      />
    )
  }

  return <>{children}</>
}
